import { ref, computed, watch, type Ref } from 'vue'
import { jsonapiClient } from '@/api/client'
import { isType, type Resource } from '@/api/resources'
import { num, toDecimal } from '@/lib/number'
import { bundledTypesOf, type BundledType } from '@/lib/typeOffers'

// « Soumission type » in the intake doors — the template face's data half.
// Given the picked TYPE, lists its named OFFERS (the type's proposal
// templates) and builds the mini price form's rows for the CHOSEN one — the
// sole offer picks itself; several wait for the user's pick. The mini form
// is a FORM, never a second builder: it reads the structure and collects
// NUMBERS — a price and a quantity per line, both prefilled where the
// template answers, both overridable (a blank template quantity = « à
// mesurer sur place », the form asks). Entered numbers belong to the DEAL
// (they ride won_template into the instantiation); the template's standing
// prices only change in its editor.
export type TemplateFaceStatus = 'loading' | 'none' | 'ready'

export interface TemplateOffer {
  id: string
  name: string
  /** « FORFAIT » — the other job types this offer also sells; empty otherwise. */
  bundledTypes: BundledType[]
}

export interface TemplatePriceRow {
  id: string
  name: string
  unit: string | null
  optional: boolean
  discountPercent: number
  // The two inputs — price prefilled from effective_unit_price, quantity from
  // the template's own quantity (null = « à mesurer », the form asks empty).
  price: number | null
  quantity: number | null
}

export interface TemplatePriceSection {
  key: string
  name: string | null
  /** A forfait's section stamped with ANOTHER job type — the form heads it
   *  with that type, so the mini form reads per type. Null = the offer's own. */
  typeId: string | null
  typeName: string | null
  rows: TemplatePriceRow[]
}

export interface TemplateLineInput { id: string; unit_price: string; quantity?: string }

export function useTemplatePricing(jobTemplateId: Ref<string | null>) {
  const status = ref<TemplateFaceStatus>('none')
  const offers = ref<TemplateOffer[]>([])
  // The chosen offer — auto when the type has exactly one, else the user's.
  const offerId = ref<string | null>(null)
  const sections = ref<TemplatePriceSection[]>([])

  const reset = () => {
    status.value = 'none'
    offers.value = []
    offerId.value = null
    sections.value = []
  }

  // The offer's structure → the form's rows. Sections follow the template's
  // group order, loose lines last (the builder's own reading order).
  const loadOffer = async (id: string, typeId: string) => {
    sections.value = []
    status.value = 'loading'
    const response = await jsonapiClient.get('/api/proposal_templates/{id}', {
      path: { id },
      params: {
        include: {
          template_lines: { template_line_group: true },
          // A FORFAIT's section names the other job type it sells.
          template_line_groups: { job_template: true },
        },
      },
    })
    if (jobTemplateId.value !== typeId || offerId.value !== id) return

    const included = response.data.included ?? []
    const lines = included
      .filter((r): r is Resource<'template_line'> => isType(r, 'template_line'))
      .sort((a, b) => (a.attributes.position ?? 0) - (b.attributes.position ?? 0))
    const groups = included
      .filter((r): r is Resource<'template_line_group'> => isType(r, 'template_line_group'))
      .sort((a, b) => (a.attributes.position ?? 0) - (b.attributes.position ?? 0))

    const rowFor = (line: Resource<'template_line'>): TemplatePriceRow => {
      const effective = line.attributes.effective_unit_price
      const quantity = line.attributes.quantity
      return {
        id: line.id,
        name: line.attributes.name ?? '',
        unit: line.attributes.unit ?? null,
        optional: line.attributes.optional ?? false,
        discountPercent: num(line.attributes.discount_percent ?? '0'),
        price: effective == null ? null : num(effective),
        quantity: quantity == null ? null : num(quantity),
      }
    }

    // A section typed with the offer's OWN type reads as untyped — only
    // another type earns a heading.
    const sectionTypeId = (group: Resource<'template_line_group'>): string | null => {
      const sectionType = group.relationships?.job_template?.data?.id ?? null
      return sectionType && sectionType !== typeId ? sectionType : null
    }

    const typeName = (tid: string | null): string | null => {
      if (!tid) return null
      const type = included.find((r): r is Resource<'job_template'> => isType(r, 'job_template') && r.id === tid)
      return type?.attributes.name ?? null
    }

    const built: TemplatePriceSection[] = groups.map(group => ({
      key: group.id,
      name: group.attributes.name,
      typeId: sectionTypeId(group),
      typeName: typeName(sectionTypeId(group)),
      rows: lines
        .filter(l => l.relationships?.template_line_group?.data?.id === group.id)
        .map(rowFor),
    }))
    const loose = lines
      .filter(l => !l.relationships?.template_line_group?.data?.id)
      .map(rowFor)
    if (loose.length) built.push({ key: 'loose', name: null, typeId: null, typeName: null, rows: loose })

    sections.value = built.filter(section => section.rows.length)
    status.value = 'ready'
  }

  watch(jobTemplateId, async (id) => {
    reset()
    if (!id) return

    status.value = 'loading'
    try {
      const typeResponse = await jsonapiClient.get('/api/job_templates/{id}', {
        path: { id },
        params: { include: { proposal_templates: true } },
      })
      if (jobTemplateId.value !== id) return // stale — the picker moved on

      const ids = new Set((typeResponse.data.data.relationships?.proposal_templates?.data ?? []).map(d => d.id))
      offers.value = (typeResponse.data.included ?? [])
        .filter((r): r is Resource<'proposal_template'> => isType(r, 'proposal_template') && ids.has(r.id))
        .sort((a, b) => (a.attributes.position ?? 0) - (b.attributes.position ?? 0))
        .map(t => ({ id: t.id, name: t.attributes.name, bundledTypes: bundledTypesOf(t) }))

      if (!offers.value.length) {
        status.value = 'none'
        return
      }
      // The sole offer picks itself — unless it is a FORFAIT: that one creates
      // other jobs, so it is never a silent default (the server agrees).
      if (offers.value.length === 1 && !offers.value[0].bundledTypes.length) {
        offerId.value = offers.value[0].id
        await loadOffer(offerId.value, id)
        return
      }
      // Several offers: the face is offered, its rows wait for the pick.
      status.value = 'ready'
    } catch {
      // Fail-soft like the demand lookup: no template face beats a blocked
      // intake — « Montant convenu » stays fully usable.
      if (jobTemplateId.value === id) status.value = 'none'
    }
  }, { immediate: true })

  const pickOffer = async (id: string) => {
    const typeId = jobTemplateId.value
    if (!typeId || offerId.value === id || !offers.value.some(o => o.id === id)) return
    offerId.value = id
    try {
      await loadOffer(id, typeId)
    } catch {
      if (offerId.value === id) {
        sections.value = []
        status.value = 'ready'
      }
    }
  }

  const rows = computed(() => sections.value.flatMap(section => section.rows))

  // Every price answered (0 is a legal price; blank is not), every quantity
  // answered, on a CHOSEN offer with lines — the submit gate, mirrored loud
  // server-side.
  const valid = computed(() =>
    status.value === 'ready'
    && offerId.value != null
    && rows.value.length > 0
    && rows.value.every(row =>
      row.price != null && row.price >= 0
      && row.quantity != null && row.quantity > 0))

  // The running before-tax total of the visible numbers — form arithmetic
  // (the created document's totals stay server-computed). Optional lines
  // excluded, per-line discount applied: catalog_total's own math.
  const total = computed<number | null>(() => {
    if (!valid.value) return null
    const sum = rows.value
      .filter(row => !row.optional)
      .reduce((acc, row) =>
        acc + (row.quantity ?? 0) * (row.price ?? 0) * (1 - row.discountPercent / 100), 0)
    return Math.round(sum * 100) / 100
  })

  // won_template's lines — every row's answer. The quantity rides even when
  // it matches the template's: what the user SAW is what lands, immune to the
  // template drifting between fetch and submit.
  const linesPayload = computed<TemplateLineInput[]>(() =>
    rows.value
      .filter(row => row.price != null)
      .map(row => ({
        id: row.id,
        unit_price: toDecimal(row.price ?? 0),
        ...(row.quantity != null ? { quantity: toDecimal(row.quantity) } : {}),
      })))

  return { status, offers, offerId, pickOffer, sections, valid, total, linesPayload, reset }
}
